// src/components/CharacterCertificate.js
import React, { useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import logo from "../assets/icon.png";

// ---------- helpers ----------
const formatIssueDate = (date = new Date()) =>
  date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }); // 02 February 2026

const formatToday = () =>
  new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const formatStudentDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
};

// ---------- certificate block ----------
const CharacterCertificate = React.forwardRef((props, ref) => {
  const {
    school = {
      name: "ACHARYAKULAM",
      address: "TATA ROAD, NAMKUM, RANCHI 834010",
    },
    session = "2025-26",
    issueDate = "",
    studentName = "",
    parents = "",
    admissionNo = "",
    className = "",
    fromDate = "",
    toDate = "",
  } = props;

  return (
    <div ref={ref} style={styles.page}>
      <div style={styles.border}>
        <div style={styles.watermarkWrap} aria-hidden="true">
          <img src={logo} alt="" style={styles.watermarkImg} />
        </div>

        <div style={styles.content}>
          <div style={styles.headerRow}>
            <div style={styles.logoBox}>
              <img src={logo} alt="logo" style={styles.logoImg} />
            </div>

            <div style={styles.headerText}>
              <div style={styles.schoolName}>{school.name}</div>
              <div style={styles.schoolAddress}>{school.address}</div>
              <div style={styles.session}>Session ({session})</div>
            </div>

            <div style={styles.logoBox} />
          </div>

          <div style={styles.pillWrap}>
            <div style={styles.pill}>CHARACTER CERTIFICATE</div>
          </div>

          <div style={styles.bigHeading}>TO WHOMSOEVER IT MAY CONCERN</div>
          <div style={styles.hr} />

          <div style={styles.bodyText}>
            This is to certify that Master/Miss : <b>{studentName}</b>
            <br />
            S/O/D/O : <b>{parents}</b>
            <br />
            Admission No.: <b>{admissionNo}</b> , Class <b>{className}</b> is a
            <br />
            bonafide student of this school from : <b>
              {fromDate || "N/A"}
            </b> To <b>{formatToday}</b>
            <br />
            To the best of my knowledge he/she bears a Good Moral Character.
            <br />
            I wish him/her every success in life.
            <br />
          </div>

          <div style={styles.footerRow}>
            <div style={styles.footerCell}>
              <div style={styles.footerTop}>{issueDate}</div>
              <div style={styles.footerLabel}>Issue Date</div>
            </div>

            <div style={styles.footerCell}>
              <div style={styles.footerTop}>Dealing Clerk</div>
              <div style={styles.footerLabel}>Signature</div>
            </div>

            <div style={styles.footerCell}>
              <div style={styles.footerTop}>Principal</div>
              <div style={styles.footerLabel}>Signature</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// ---------- ONE PAGE component ----------
export default function CharacterCertificatePage({
  student,
  buttonLabel = "Character Certificate",
}) {
  const certRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  // ✅ IMPORTANT: useMemo is INSIDE component (no eslint error)
  const certData = useMemo(() => {
    // support both shapes:
    // 1) student.studentDetails.name etc (your StudentDetails page)
    // 2) flat student_name/father_name etc (your fee ledger style)

    const nameRaw =
      student?.studentDetails?.name ||
      student?.student_name ||
      student?.name ||
      "";

    const fatherRaw =
      student?.parentDetails?.[0]?.name ||
      student?.father_name ||
      student?.fatherName ||
      "";

    const motherRaw =
      student?.parentDetails?.[1]?.name ||
      student?.mother_name ||
      student?.motherName ||
      "";

    const admissionNo =
      student?.admission_no ||
      student?.admissionNo ||
      student?.studentDetails?.admission_no ||
      "";

    const className =
      student?.currentClass ||
      student?.class ||
      student?.class_name ||
      student?.className ||
      "";

    const fromDate = formatStudentDate(
      student?.studentDetails?.admissionDate ||
        student?.admission_date ||
        student?.admissionDate ||
        student?.join_date
    );

    const toDate = formatStudentDate(
      student?.tc_date || student?.leaving_date || student?.leaveDate
    );

    const session = student?.session || student?.academic_session || "2025-26";

    return {
      session,
      issueDate: formatIssueDate(new Date()),
      studentName: String(nameRaw).toUpperCase(),
      parents: [fatherRaw, motherRaw].filter(Boolean).join(" & ").toUpperCase(),
      admissionNo: String(admissionNo),
      className: String(className).toUpperCase(),
      fromDate,
      toDate,
    };
  }, [student]);

  const downloadPdf = async () => {
    try {
      if (!student) {
        console.error("CharacterCertificatePage: student missing");
        return;
      }
      if (!certRef.current) return;

      setDownloading(true);

      const canvas = await html2canvas(certRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("l", "mm", "a4");
      const pageW = 297;
      const pageH = 210;

      const margin = 10;
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;

      const imgRatio = canvas.width / canvas.height;
      let renderW = usableW;
      let renderH = renderW / imgRatio;

      if (renderH > usableH) {
        renderH = usableH;
        renderW = renderH * imgRatio;
      }

      const x = (pageW - renderW) / 2;
      const y = (pageH - renderH) / 2;

      pdf.addImage(imgData, "PNG", x, y, renderW, renderH);
      pdf.save(
        `${certData.admissionNo || "student"}-character-certificate.pdf`
      );
    } catch (e) {
      console.error("Character certificate download failed:", e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="w-100">
      {/* hidden render only for pdf */}
      <div style={{ position: "fixed", left: "-99999px", top: 0 }}>
        <CharacterCertificate ref={certRef} {...certData} />
      </div>

      <div className="mt-4 mb-2">
        <span
          className="bg-dark-soft fs-5 fw-normal rounded-pill ps-4 pe-5 py-2 cursor-pointer"
          onClick={downloading ? undefined : downloadPdf}
          style={{ opacity: downloading ? 0.6 : 1 }}
        >
          <i className="bi bi-download me-1"></i>{" "}
          {downloading ? "Downloading" : buttonLabel}
        </span>
      </div>
    </div>
  );
}

// ---------- styles ----------
const styles = {
  page: {
    width: "1123px",
    height: "794px",
    boxSizing: "border-box",
    background: "#fff",
    padding: "14px",
    fontFamily: "Arial, Helvetica, sans-serif",
    color: "#111",
  },

  border: {
    height: "100%",
    boxSizing: "border-box",
    border: "3px solid #111",
    padding: "30px",
    display: "flex",
    flexDirection: "column",
    position: "relative",
  },

  content: {
    position: "relative",
    zIndex: 3,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },

  watermarkWrap: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
    pointerEvents: "none",
  },
  watermarkImg: {
    width: "380px",
    height: "380px",
    objectFit: "contain",
    opacity: 0.08,
    filter: "grayscale(100%)",
  },

  headerRow: {
    display: "grid",
    gridTemplateColumns: "90px 1fr 90px",
    alignItems: "center",
  },
  logoBox: {
    width: "90px",
    height: "90px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoImg: { width: "95px", height: "95px", objectFit: "contain" },
  headerText: { textAlign: "center" },
  schoolName: { fontSize: "30px", fontWeight: 800, letterSpacing: "1px" },
  schoolAddress: { fontSize: "23px", marginTop: "-4px" },
  session: { fontSize: "23px", marginTop: "-4px" },

  pillWrap: { display: "flex", justifyContent: "center", marginTop: "13px" },
  pill: {
    border: "2px solid #111",
    borderRadius: "12px",
    padding: "6px 70px",
    fontWeight: 700,
    fontSize: "16px",
  },

  bigHeading: {
    marginTop: "16px",
    textAlign: "center",
    fontSize: "26px",
    fontWeight: 800,
  },
  hr: { height: "1px", background: "#d8d8d8", marginTop: "10px" },

  bodyText: {
    marginTop: "30px",
    fontSize: "30px",
    lineHeight: "1.3",
    textAlign: "center",
    fontStyle: "italic",
    padding: "0 60px",
  },

  footerRow: {
    marginTop: "auto",
    display: "flex",
    justifyContent: "space-between",
    paddingTop: "35px",
    paddingBottom: "20px",
  },

  footerCell: { width: "33%", textAlign: "center" },
  footerTop: { fontSize: "17px", fontWeight: 700 },
  footerLabel: { marginTop: "-2px", fontSize: "17px", fontWeight: 700 },
};
